import './replace-password.screen.scss';
import {MutableRefObject, useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Divider, Typography, Button, Box} from '@mui/material';
import {PublicContentPage} from '../../../../components/public-content-page/public-content-page';
import {LegalLinksStack} from '../../../../components/legal-links-stack/legal-links-stack';
import {useHistory, useLocation} from 'react-router-dom';
import {ScreenPaths} from '../../../../enums/screen-path';
import {translations} from '../../../../i18n/translations';
import {SnackBarContextType} from '../../../../contexts/snack-bar/types';
import {SnackBarContext} from '../../../../contexts/snack-bar/context';
import {PASSWORD_REGEX} from '../../../../utils/constants/regex';
import {AuthenticationRepository} from '../../../../repositories/authentication';
import {PatchReplacePassword} from '../../../../backend/requests/patch-replace-password';
import {PasswordInput} from '../../../../components/password-input/password-input';
import LoadingButton from '@mui/lab/LoadingButton';
import SendIcon from '@mui/icons-material/Send';

export default function ReplacePasswordScreen() {
  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);

  // Translations
  const {t} = useTranslation();

  // Get history and query parameters
  let history = useHistory();
  let {search} = useLocation();

  // Hold ref
  const tokenRef: MutableRefObject<string> = useRef('');

  // Hold state
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordError, setNewPasswordError] = useState<string | undefined>();
  const [showNewPasswordError, setShowNewPasswordError] = useState<boolean>(false);
  const [repeatPassword, setRepeatPassword] = useState<string>('');
  const [repeatPasswordError, setRepeatPasswordError] = useState<string | undefined>();
  const [showRepeatPasswordError, setShowRepeatPasswordError] = useState<boolean>(false);

  // Get the token
  useEffect(() => {
    if (search) {
      // Get token
      const splitToken: string | undefined = search.split('?token=')[1];

      // Set token
      if (splitToken !== undefined && splitToken.length > 0) {
        tokenRef.current = splitToken;
      }
    }
  }, [search]);

  // Validation
  useEffect(() => {
    // Holds validation
    let newPasswordValid: boolean = false;
    let repeatPasswordValid: boolean = false;

    if (newPassword.length > 0) {
      if (newPassword.trim().length !== newPassword.length) {
        if (showNewPasswordError) {
          // Error
          setNewPasswordError(
            t(translations.replace_password.errors.form_validation_invalid_password_blank_spaces)
          );
        }

        // Set it
        newPasswordValid = false;
      } else {
        if (PASSWORD_REGEX.test(newPassword)) {
          if (newPasswordError !== undefined) {
            // No errors
            setNewPasswordError(undefined);
          }

          // Set it
          newPasswordValid = true;
        } else {
          if (showNewPasswordError) {
            // Error
            setNewPasswordError(
              t(
                translations.replace_password.errors
                  .form_validation_invalid_password_minimum_characters
              )
            );
          }

          // Set it
          newPasswordValid = false;
        }
      }
    } else {
      if (showNewPasswordError) {
        // Error
        setNewPasswordError(t(translations.replace_password.errors.form_validation_invalid_field));
      }

      // Set it
      newPasswordValid = false;
    }

    // Repeat password validation
    if (repeatPassword.length > 0) {
      if (repeatPassword === newPassword) {
        if (repeatPasswordError !== undefined) {
          // No errors
          setRepeatPasswordError(undefined);
        }

        // Set it
        repeatPasswordValid = true;
      } else {
        if (showRepeatPasswordError) {
          // Error
          setRepeatPasswordError(
            t(translations.replace_password.errors.form_validation_invalid_repeat_password_mismatch)
          );
        }

        // Set it
        repeatPasswordValid = false;
      }
    } else {
      if (showRepeatPasswordError) {
        // Error
        setRepeatPasswordError(
          t(translations.replace_password.errors.form_validation_invalid_field)
        );
      }

      // Set it
      repeatPasswordValid = false;
    }

    // Submit button validation
    if (newPasswordValid && repeatPasswordValid) {
      if (buttonDisabled !== false) {
        // Enable button
        setButtonDisabled(false);
      }
    } else {
      if (buttonDisabled !== true) {
        // Disable button
        setButtonDisabled(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPassword, repeatPassword, showNewPasswordError, showRepeatPasswordError]);

  // Replace password
  const handleReplacePassword = async (event: any) => {
    event.preventDefault();

    // Reset form errors
    setNewPasswordError(undefined);
    setRepeatPasswordError(undefined);

    // Everything looks ok. Let's perform the request. Start loading.
    setSubmitting(true);

    try {
      // Request body
      const requestBody: PatchReplacePassword = {
        token: tokenRef.current,
        newPassword: newPassword,
      };

      // Send request
      await AuthenticationRepository.instance.replace(requestBody);

      // Everything went well
      snackBarContext.setPublicToastNotification({
        message: t(translations.replace_password.success.change_password_success_message),
        severity: 'success',
      });

      // Go to the login screen
      history.replace(ScreenPaths.Login);
    } catch (error) {
      // Nothing went well
      snackBarContext.setPublicToastNotification({
        message: t(translations.replace_password.errors.change_password_error_message),
        severity: 'error',
      });
    }

    // Stop loading
    setSubmitting(false);
  };

  // Navigate to the login screen
  const handleNavigateToLogin = () => {
    // Navigate
    history.replace(ScreenPaths.Login);
  };

  // Handle blur new password input
  const handleBlurNewPassword = () => {
    if (!showNewPasswordError) {
      setShowNewPasswordError(true);
    }
  };

  // Handle change new password input
  const handleChangeNewPassword = (value: string | undefined | null) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Set new password with the new value
    setNewPassword(finalValue);
  };

  // Handle blur repeat password input
  const handleBlurRepeatPassword = () => {
    if (!showRepeatPasswordError) {
      setShowRepeatPasswordError(true);
    }
  };

  // Handle change repeat password input
  const handleChangeRepeatPassword = (value: string | undefined | null) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Set repeat password with the new value
    setRepeatPassword(finalValue);
  };

  return (
    <PublicContentPage>
      <Typography variant="h6" sx={{fontWeight: 'bold'}} className="replace-password-title">
        {t(translations.replace_password.title)}
      </Typography>
      <Typography variant="body1" className="replace-password-instructions">
        {t(translations.replace_password.instructions)}
      </Typography>
      <form onSubmit={handleReplacePassword}>
        <PasswordInput
          label={t(translations.replace_password.new_password_input as string)}
          value={newPassword}
          handleBlur={handleBlurNewPassword}
          handleChange={(value) => handleChangeNewPassword(value)}
          error={newPasswordError}
          className="replace-password-input"
          disabled={submitting}
          required={true}
        />
        <PasswordInput
          label={t(translations.replace_password.repeat_password_input as string)}
          value={repeatPassword}
          handleBlur={handleBlurRepeatPassword}
          handleChange={(value) => handleChangeRepeatPassword(value)}
          error={repeatPasswordError}
          className="replace-password-input"
          disabled={submitting}
          required={true}
        />
        <LoadingButton
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          size="medium"
          loading={submitting}
          loadingPosition="end"
          endIcon={<SendIcon />}
          disabled={buttonDisabled}
        >
          {t(translations.replace_password.submit_button)}
        </LoadingButton>
      </form>
      <Divider className="replace-password-divider" />
      <Box className="replace-password-remembered-container">
        <Typography className="replace-password-remembered-title" variant="body1">
          {t(translations.replace_password.password_remembered)}
        </Typography>
        <Button
          disabled={submitting}
          variant="text"
          color="inherit"
          size="medium"
          className="replace-password-remembered-button"
          onClick={handleNavigateToLogin}
        >
          {t(translations.replace_password.go_back_button)}
        </Button>
      </Box>
      <LegalLinksStack paddingTop="44px" />
    </PublicContentPage>
  );
}
